import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../../services/Employee";
import { getUserAccess } from "../../../utils/authUtils";
import { HrmsPageShell } from "../../../components/Hrms/HrmsPageShell";
import { HrmsTable } from "../../../components/Hrms/HrmsTable";
import { HOLIDAY_TYPE_META } from "../../../components/Hrms/HolidayCalendarModal";
import { hrmsDateBounds, validateHolidayDate } from "../hrmsDateRules";

interface HolidayRow {
  _id: string;
  name: string;
  date: string;
  type: string;
  year: number;
}

const emptyForm = { name: "", date: "", type: "national" };

export const HolidayMaster = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const { canManageConfig } = getUserAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [holidays, setHolidays] = useState<HolidayRow[]>([]);
  const [isNew, setIsNew] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const bounds = hrmsDateBounds();

  const fetchHolidays = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getHolidays(token);
      if (res.data.status === "success") {
        setHolidays(res.data.data || []);
      }
    } catch (e: any) {
      console.error("Error fetching holidays:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (holiday?: HolidayRow) => {
    if (holiday) {
      setSelectedId(holiday._id);
      setForm({ name: holiday.name, date: holiday.date, type: holiday.type });
      setIsNew(false);
    } else {
      setSelectedId("");
      setForm(emptyForm);
      setIsNew(true);
    }
    onOpen();
  };

  const handleSave = async () => {
    if (!form.name || !form.date) {
      toast({
        position: "top",
        title: "Name and date are required",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const dateError = validateHolidayDate(form.date);
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    setIsSaving(true);
    try {
      const res = isNew
        ? await EmployeeDataService.createHoliday(token, form, userEmail)
        : await EmployeeDataService.updateHoliday(
            token,
            selectedId,
            form,
            userEmail,
          );

      if (res.data.status === "success") {
        toast({
          position: "top",
          title: `Holiday ${isNew ? "added" : "updated"}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        fetchHolidays();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to save holiday.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await EmployeeDataService.deleteHoliday(token, id, userEmail);
      fetchHolidays();
    } catch (e: any) {
      console.error("Error deleting holiday:", e);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  return (
    <HrmsPageShell
      title="Holiday Master"
      description="Holidays and weekly offs shown in every employee's Holiday Calendar"
      sectionKey="configuration"
      noPermission={!canManageConfig}
      isLoading={canManageConfig && isLoading}
      actions={
        canManageConfig ? (
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={() => openEdit()}
          >
            Add Holiday
          </Button>
        ) : undefined
      }
    >
      <HrmsTable
        head={["Name", "Date", "Type", "Action"]}
        isEmpty={holidays.length === 0}
        emptyText="No holidays configured"
      >
        {holidays.map((h) => (
          <tr key={h._id}>
            <td>{h.name}</td>
            <td style={{ whiteSpace: "nowrap" }}>
              {DateTime.fromISO(h.date).toFormat("dd MMM yyyy")}
              <span style={{ color: "#718096", marginLeft: 8, fontSize: 12 }}>
                {DateTime.fromISO(h.date).toFormat("ccc")}
              </span>
            </td>
            <td>
              <Badge colorScheme={HOLIDAY_TYPE_META[h.type]?.color || "gray"}>
                {HOLIDAY_TYPE_META[h.type]?.label || h.type}
              </Badge>
            </td>
            <td>
              <Button
                size="xs"
                bg="#327171"
                color="white"
                _hover={{ bg: "#285e5e" }}
                mr={2}
                onClick={() => openEdit(h)}
              >
                Edit
              </Button>
              <Button
                size="xs"
                colorScheme="red"
                onClick={() => handleDelete(h._id)}
              >
                Delete
              </Button>
            </td>
          </tr>
        ))}
      </HrmsTable>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isNew ? "Add Holiday" : "Edit Holiday"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Name</FormLabel>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Date</FormLabel>
              <Input
                type="date"
                value={form.date}
                min={bounds.holiday.min}
                max={bounds.holiday.max}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <Select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="national">National</option>
                <option value="restricted">Restricted</option>
                <option value="optional">Optional</option>
                <option value="weekly_off">Weekly Off</option>
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={handleSave}
              isLoading={isSaving}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
